// review-state-store.ts
//
// Projection review-state: `<control_root>/review-state/<repo_key>/<candidate_sha>.json`
// (§9.3). Đây là NƠI DUY NHẤT push-gate đọc để biết một SHA đã được review chưa.
//
// Hai luật an ninh cốt tử:
//  1. `repo_key` do CONTROLLER sinh từ identity ĐÃ CHỐT (hash của path canonical) —
//     KHÔNG BAO GIỜ nhận path/khoá do worker cung cấp. Nhận path từ worker là mở đường
//     path traversal: `../../` trong repo_key sẽ ghi receipt ra ngoài control root.
//  2. Receipt RÀNG BUỘC mission + repo + head + base + policy digest. Receipt của
//     mission khác (hoặc SHA/base/policy khác) KHÔNG mở được gate — kiểm bằng so khớp
//     toàn bộ, không chỉ SHA.
//
// Receipt là projection ⇒ dựng lại được từ journal sau restart (test 54).

import { createHash } from "node:crypto";
import { promises as fs } from "node:fs";
import { join, resolve } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export interface ReviewReceipt {
  mission_id: string;
  repo_key: string;
  repo_path: string;
  candidate_sha: string;
  base_sha: string;
  policy_digest: string;
  adapter_digest: string;
  review_risk: string;
  secret_scan: { verdict: string; findings: number };
  evidence_hashes: string[];
  event_seq: number;
  written_at: string;
}

/**
 * Khoá thư mục cho một repo — sinh từ path CANONICAL, không nhận từ worker.
 * Dùng hash nên kết quả luôn là một segment an toàn (không `/`, không `..`).
 */
export function deriveRepoKey(repoPath: string): string {
  const canonical = resolve(repoPath);
  const digest = createHash("sha256").update(canonical, "utf8").digest("hex").slice(0, 16);
  const readable = canonical.split("/").filter(Boolean).pop() ?? "repo";
  const safeReadable = readable.replace(/[^A-Za-z0-9._-]/g, "-").slice(0, 40);
  return `${safeReadable}-${digest}`;
}

export function reviewStateRoot(controlRoot: string): string {
  return join(controlRoot, "review-state");
}

export function receiptPath(controlRoot: string, repoPath: string, candidateSha: string): string {
  if (!/^[0-9a-f]{7,64}$/i.test(candidateSha)) {
    throw new Error(`candidate_sha không hợp lệ: ${JSON.stringify(candidateSha)}`);
  }
  return join(reviewStateRoot(controlRoot), deriveRepoKey(repoPath), `${candidateSha}.json`);
}

/** Ghi receipt BỀN. Controller sở hữu bước này — worker không bao giờ gọi. */
export async function writeReviewReceipt(
  controlRoot: string,
  receipt: ReviewReceipt,
): Promise<string> {
  const path = receiptPath(controlRoot, receipt.repo_path, receipt.candidate_sha);
  await ensureDir(join(path, ".."));
  await writeFileDurable(path, Buffer.from(`${JSON.stringify(receipt, null, 2)}\n`, "utf8"));
  return path;
}

export async function readReviewReceipt(
  controlRoot: string,
  repoPath: string,
  candidateSha: string,
): Promise<ReviewReceipt | undefined> {
  try {
    const path = receiptPath(controlRoot, repoPath, candidateSha);
    return JSON.parse(await fs.readFile(path, "utf8")) as ReviewReceipt;
  } catch {
    return undefined;
  }
}

export interface ReceiptExpectation {
  missionId: string;
  repoPath: string;
  candidateSha: string;
  baseSha: string;
  policyDigest: string;
}

export interface ReceiptCheck {
  ok: boolean;
  reason: string;
}

/**
 * Receipt có mở được gate cho mong đợi này không. So khớp TOÀN BỘ identity — thiếu
 * bất kỳ vế nào (mission/repo/head/base/policy) đều phải re-review (§9.3).
 */
export function receiptOpensGate(
  receipt: ReviewReceipt | undefined,
  expected: ReceiptExpectation,
): ReceiptCheck {
  if (receipt === undefined) {
    return { ok: false, reason: "Không có review receipt cho SHA này ⇒ chưa được review." };
  }
  if (receipt.mission_id !== expected.missionId) {
    return {
      ok: false,
      reason:
        `Receipt thuộc mission "${receipt.mission_id}" ≠ mission hiện tại ` +
        `"${expected.missionId}" ⇒ KHÔNG mở gate (§9.3).`,
    };
  }
  if (receipt.repo_key !== deriveRepoKey(expected.repoPath)) {
    return { ok: false, reason: "Receipt thuộc repo khác ⇒ không mở gate." };
  }
  if (receipt.candidate_sha !== expected.candidateSha) {
    return { ok: false, reason: "Receipt cho candidate SHA khác ⇒ phải review lại." };
  }
  if (receipt.base_sha !== expected.baseSha) {
    return {
      ok: false,
      reason:
        `Base SHA đã đổi (receipt: ${receipt.base_sha}, hiện tại: ${expected.baseSha}) ⇒ ` +
        "diff đã review không còn là diff sắp đẩy, phải review lại.",
    };
  }
  if (receipt.policy_digest !== expected.policyDigest) {
    return { ok: false, reason: "Policy digest đã đổi kể từ lúc review ⇒ phải review lại." };
  }
  if (receipt.secret_scan.verdict !== "clean") {
    return {
      ok: false,
      reason: `Secret scan chưa sạch (verdict=${receipt.secret_scan.verdict}) ⇒ chặn.`,
    };
  }
  return { ok: true, reason: "Receipt khớp mission/repo/head/base/policy và secret scan sạch." };
}
